import { observer } from 'mobx-react'
import React, { useRef, useState } from 'react'
import { GlobalHotKeys } from 'react-hotkeys'

import { LocationValue } from '@flowwai-frontend/shared/common-components'
import { keyboardShortcuts } from '@flowwai-frontend/shared/utils/web'

import { MapsFeaturesEnum } from '../../constants'
import { useFitBoundsInMap } from '../../hooks/useFitBoundsInMap'
import { useMapsEventHandlers } from '../../hooks/useMapsEventHandlers'
import { useMapsInstanceContext } from '../../hooks/useMapsInstanceContext'
import { useMapsUIStore } from '../../hooks/useMapsUIStore'
import { useMapsUtilityContext } from '../../hooks/useMapsUtilityContext'
import { useUpdateGeoShape } from '../../hooks/useUpdateGeoShape'
import { MapCoordinates } from '../../types'
import { getCoordinatesForGeoShape } from '../../utils/GeoFieldsUtil'
import CoordinatesListWithSearch from '../CoordinatesListWithSearch/CoordinatesListWithSearch'
import GoogleMapsLayers from '../GoogleMapsLayers/GoogleMapsLayers'
import MapboxLayers from '../MapboxLayers/MapboxLayers'
import MapMarker from '../MapMarker/MapMarker'
import MapMarkerPopup from '../MapMarkerPopup/MapMarkerPopup'
import MapsToolbar from '../MapsToolbar/MapsToolbar'
import SearchedLocationMarker from '../SearchedLocationMarker/SearchedLocationMarker'

interface Props {
   geoShape: LocationValue
   isReadOnly: boolean
   onUpdateGeoShape: (geoShape: LocationValue) => void
   onCloseModal: () => void

   addressCoordinates?: MapCoordinates | null
}

const MapCoordinatesWithToolbar = (props: Props): React.ReactElement => {
   const [localGeoShape, setLocalGeoShape] = useState<LocationValue>(
      props.geoShape
   )
   const historyRef = useRef<LocationValue[]>([props.geoShape])

   const [selectedMarkerIndex, setSelectedMarkerIndex] = useState<
      number | null
   >(null)
   const [
      searchedLocation,
      setSearchedLocation
   ] = useState<MapCoordinates | null>(null)

   const { serviceProvidersConfig, supportedFeatures } = useMapsUtilityContext()
   const mapsInstance = useMapsInstanceContext()

   const isReadOnly = !mapsInstance.isEditMode || props.isReadOnly

   const shouldRenderCoordinatesList = supportedFeatures?.includes(
      MapsFeaturesEnum.CoordinatesList
   )

   const isGeoShapeChanged =
      JSON.stringify(localGeoShape) !== JSON.stringify(props.geoShape)

   const updateLocalGeoShapeWithHistory = (geoShape: LocationValue): void => {
      historyRef.current = [...historyRef.current, geoShape]
      setLocalGeoShape(geoShape)
   }

   useFitBoundsInMap({
      currentGeoShape: localGeoShape,
      addressCoordinates: props.addressCoordinates
   })

   const closePopoverIfOpen = (): boolean => {
      if (selectedMarkerIndex !== null) {
         setSelectedMarkerIndex(null)
         return true
      }
      return false
   }

   const {
      validateAndUpdateGeoShape,
      onAddCoordinates,
      onDeleteCoordinates,
      onMoveMarker,
      onUndo,
      errorMessage
   } = useUpdateGeoShape({
      geoShape: localGeoShape,
      historyRef,
      onUpdateGeoShape: updateLocalGeoShapeWithHistory,
      selectedMarkerIndex,
      setSelectedMarkerIndex
   })

   useMapsEventHandlers({
      currentGeoShape: localGeoShape,
      isReadOnly: props.isReadOnly,
      validateAndUpdateGeoShape,
      closePopoverIfOpen
   })

   const markerCoordinates = getCoordinatesForGeoShape(localGeoShape)
   const shouldShowMarkerNumber = localGeoShape.type !== 'POINT'

   const onSaveLocation = () => {
      props.onUpdateGeoShape(localGeoShape)
      props.onCloseModal()
   }

   const handleOnMarkerClick = (index: number) => {
      const isActiveMarker = selectedMarkerIndex === index

      if (isActiveMarker) setSelectedMarkerIndex(null)
      else setSelectedMarkerIndex(index)
   }

   const onSearchLocation = (coordinates: MapCoordinates): void => {
      setSearchedLocation(coordinates)

      mapsInstance.panTo(coordinates)
   }

   const MapLayersComponent =
      serviceProvidersConfig.maps.serviceProvider === 'GOOGLE_MAPS'
         ? GoogleMapsLayers
         : MapboxLayers

   const keyMap = { ON_UNDO: [keyboardShortcuts.undo] }

   const handlers = { ON_UNDO: onUndo }

   return (
      <GlobalHotKeys keyMap={keyMap} handlers={handlers}>
         <React.Fragment>
            {markerCoordinates.map((eachCoordinate, index) => (
               <React.Fragment>
                  <MapMarker
                     coordinates={eachCoordinate}
                     isDraggable={!isReadOnly}
                     markerNumber={index + 1}
                     shouldShowMarkerNumber={shouldShowMarkerNumber}
                     onMove={updatedCoordinates =>
                        onMoveMarker(updatedCoordinates, index)
                     }
                     onClick={() => handleOnMarkerClick(index)}
                  />

                  {selectedMarkerIndex === index && (
                     <MapMarkerPopup
                        index={index}
                        coordinates={eachCoordinate}
                        isReadOnly={isReadOnly}
                        onUpdateCoordinate={onMoveMarker}
                        onDeleteCoordinates={() => onDeleteCoordinates(index)}
                     />
                  )}
               </React.Fragment>
            ))}

            <SearchedLocationMarker
               isReadOnly={props.isReadOnly}
               searchedLocation={searchedLocation}
               selectedMarkerIndex={selectedMarkerIndex}
               setSelectedMarkerIndex={setSelectedMarkerIndex}
               setSearchedLocation={setSearchedLocation}
               onAddCoordinates={onAddCoordinates}
            />

            {shouldRenderCoordinatesList ? (
               <CoordinatesListWithSearch
                  isReadOnly={isReadOnly}
                  geoShape={localGeoShape}
                  validateAndUpdateGeoShape={validateAndUpdateGeoShape}
                  onAddCoordinates={onAddCoordinates}
                  onMoveMarker={onMoveMarker}
                  onDeleteCoordinates={onDeleteCoordinates}
                  onSearchLocation={onSearchLocation}
               />
            ) : null}

            <MapLayersComponent geoShape={localGeoShape} />

            <MapsToolbar
               geoShape={localGeoShape}
               onSaveLocation={onSaveLocation}
               errorMessage={errorMessage}
               isReadOnly={props.isReadOnly}
               isGeoShapeChanged={isGeoShapeChanged}
               onSearchLocation={onSearchLocation}
               onUndo={onUndo}
               onCloseModal={props.onCloseModal}
               addressCoordinates={props.addressCoordinates}
            />
         </React.Fragment>
      </GlobalHotKeys>
   )
}

export default observer(MapCoordinatesWithToolbar)
